// src/pages/membres/ImportExport.jsx
import React, { useState, useEffect, useRef } from 'react';
import { Row, Col, Card, Button, Alert, Spinner, Table, Form } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import * as XLSX from 'xlsx';
import Header from '../../layouts/Header';
import Footer from '../../layouts/Footer';
import { COLORS } from '../../constants/colors';

const styles = `
  .dashboard-styled {
    background: #f4f6f9;
    min-height: 100vh;
  }

  /* En-tête moderne */
  .header-modern {
    background: white;
    padding: 1.2rem 1.8rem;
    box-shadow: 0 2px 8px rgba(0,0,0,0.04);
    border-left: 4px solid ${COLORS.secondary};
    transition: all 0.2s ease;
    display: flex;
    align-items: center;
    justify-content: space-between;
    flex-wrap: wrap;
  }

  .header-left {
    display: flex;
    align-items: center;
    gap: 1rem;
  }

  .header-icon-wrapper {
    width: 48px;
    height: 48px;
    background: ${COLORS.primary};
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 4px;
    color: white;
    font-size: 1.4rem;
  }

  .gradient-text {
    color: #1e293b;
    font-size: 1.3rem;
    font-weight: 700;
    margin: 0;
  }

  .badge-modern {
    background: #f1f4f9;
    color: #475569;
    padding: 0.3rem 0.8rem;
    border-radius: 4px;
    font-size: 0.75rem;
    font-weight: 600;
    display: inline-flex;
    align-items: center;
    gap: 6px;
  }
  .badge-modern i {
    color: ${COLORS.secondary};
  }

  .btn-modern-outline {
    border: 1px solid #d1d5db;
    background: white;
    padding: 0.4rem 1.2rem;
    border-radius: 4px;
    font-size: 0.85rem;
    font-weight: 500;
    color: #4b5563;
    transition: all 0.2s;
    display: inline-flex;
    align-items: center;
    gap: 6px;
  }
  .btn-modern-outline:hover {
    border-color: ${COLORS.secondary};
    color: ${COLORS.secondary};
    background: #fafafa;
  }

  .btn-modern-primary {
    background: ${COLORS.primary};
    border: none;
    padding: 0.4rem 1.2rem;
    border-radius: 4px;
    font-size: 0.85rem;
    font-weight: 500;
    color: white;
    display: inline-flex;
    align-items: center;
    gap: 6px;
    transition: all 0.2s;
  }
  .btn-modern-primary:hover {
    background: ${COLORS.primaryDark};
    box-shadow: 0 4px 10px rgba(41,90,167,0.3);
  }

  .btn-modern-success {
    background: ${COLORS.success};
    border: none;
    padding: 0.4rem 1.2rem;
    border-radius: 4px;
    font-size: 0.85rem;
    font-weight: 500;
    color: white;
    display: inline-flex;
    align-items: center;
    gap: 6px;
    transition: all 0.2s;
  }
  .btn-modern-success:hover {
    background: #059669;
    box-shadow: 0 4px 10px rgba(16,185,129,0.3);
  }

  /* Cartes */
  .card-modern {
    border: none;
    border-radius: 4px;
    background: white;
    box-shadow: 0 2px 6px rgba(0,0,0,0.04);
    border-top: 3px solid transparent;
    transition: box-shadow 0.2s;
    height: 100%;
  }
  .card-modern:hover {
    box-shadow: 0 4px 12px rgba(0,0,0,0.06);
  }
  .card-modern.border-primary {
    border-top-color: ${COLORS.primary};
  }
  .card-modern.border-secondary {
    border-top-color: ${COLORS.secondary};
  }

  .card-header-modern {
    background: transparent;
    border-bottom: 1px solid #e9edf2;
    padding: 0.8rem 1.2rem;
    font-weight: 600;
    color: #1e293b;
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }
  .card-header-modern i {
    color: ${COLORS.secondary};
  }

  .card-body-modern {
    padding: 1.5rem;
  }

  /* Form */
  .form-label-modern {
    font-size: 0.7rem;
    font-weight: 600;
    color: #475569;
    margin-bottom: 0.2rem;
  }
  .form-label-modern .required {
    color: ${COLORS.danger};
    margin-left: 2px;
  }

  .form-control-modern, .form-select-modern {
    border: 1px solid #e2e8f0;
    border-radius: 4px;
    padding: 0.4rem 0.7rem;
    font-size: 0.85rem;
    transition: all 0.2s ease;
    background: white;
  }
  .form-control-modern:focus, .form-select-modern:focus {
    border-color: ${COLORS.primary};
    box-shadow: 0 0 0 3px rgba(41, 90, 167, 0.08);
  }

  .form-text-modern {
    font-size: 0.65rem;
    color: #94a3b8;
    margin-top: 0.2rem;
  }

  /* Table preview */
  .table-preview {
    max-height: 200px;
    overflow-y: auto;
  }
  .table-preview th {
    background: #f8fafc;
    font-size: 0.7rem;
    text-transform: uppercase;
    font-weight: 600;
    color: #64748b;
    border-bottom: 1px solid #e2e8f0;
  }
  .table-preview td {
    font-size: 0.8rem;
    padding: 0.4rem 0.6rem;
  }

  /* Icones */
  .icon-big {
    font-size: 3rem;
    color: ${COLORS.primary};
    opacity: 0.4;
  }
  .icon-big-success {
    font-size: 3rem;
    color: ${COLORS.success};
    opacity: 0.4;
  }

  /* Animations */
  @keyframes fadeIn {
    from { opacity: 0; transform: translateY(6px); }
    to { opacity: 1; transform: translateY(0); }
  }
  .fade-in {
    animation: fadeIn 0.3s ease-out;
  }

  @media (max-width: 768px) {
    .header-modern {
      flex-direction: column;
      align-items: stretch;
      gap: 0.8rem;
      padding: 1rem;
    }
    .header-left {
      flex-wrap: wrap;
    }
  }
`;

export default function ImportExport() {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const [loadingEglises, setLoadingEglises] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [importData, setImportData] = useState([]);
  const [importStep, setImportStep] = useState('upload');
  const [eglises, setEglises] = useState([]);
  const [selectedEglise, setSelectedEglise] = useState('');

  useEffect(() => {
    fetchEglises();
  }, []);

  const fetchEglises = async () => {
    try {
      setLoadingEglises(true);
      const userStr = localStorage.getItem('user');
      if (!userStr) {
        navigate('/pages/signin2');
        return;
      }
      const user = JSON.parse(userStr);
      const userId = user.id || user.id_utilisateur;

      const response = await fetch(`https://josephsbusiness.com/api/eglises.php?user_id=${userId}`, {
        headers: { 'Accept': 'application/json' }
      });
      const result = await response.json();

      if (result.success) {
        setEglises(result.eglises || []);
        if (result.eglises.length > 0) {
          setSelectedEglise(result.eglises[0].matricule);
        }
      } else {
        setError("Impossible de charger la liste des temples");
      }
    } catch (err) {
      console.error('Erreur chargement églises:', err);
      setError("Erreur de connexion pour le chargement des temples");
    } finally {
      setLoadingEglises(false);
    }
  };

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (evt) => {
      try {
        const data = new Uint8Array(evt.target.result);
        const workbook = XLSX.read(data, { type: 'array' });
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];
        const json = XLSX.utils.sheet_to_json(worksheet);

        setImportData(json.slice(0, 10));
        setImportStep('preview');
      } catch (err) {
        setError('Erreur lors de la lecture du fichier');
      }
    };
    reader.readAsArrayBuffer(file);
  };

  const handleExport = async () => {
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const userStr = localStorage.getItem('user');
      if (!userStr) {
        navigate('/pages/signin2');
        return;
      }
      const user = JSON.parse(userStr);
      const userId = user.id || user.id_utilisateur;

      const response = await fetch(`https://josephsbusiness.com/api/membres.php?user_id=${userId}`, {
        headers: { 'Accept': 'application/json' }
      });

      if (!response.ok) throw new Error(`Erreur HTTP: ${response.status}`);

      const result = await response.json();

      if (result.success) {
        const membres = result.membres || [];
        const membresAvecEglise = membres.map(m => {
          const eglise = eglises.find(e => e.matricule === m.matri_eglise);
          return {
            Matricule: m.matricule || '',
            Nom: m.nom || '',
            Prénom: m.prenom || '',
            Rôle: m.role || '',
            Genre: m.gendre || '',
            Contact: m.contact || '',
            Email: m.email || '',
            'Date naissance': m.datenaiss || '',
            Temple: eglise ? eglise.nom : m.matri_eglise || '',
            'Date création': m.date_creation || ''
          };
        });

        const ws = XLSX.utils.json_to_sheet(membresAvecEglise);
        ws['!cols'] = [
          { wch: 15 }, { wch: 20 }, { wch: 20 }, { wch: 15 },
          { wch: 10 }, { wch: 15 }, { wch: 25 }, { wch: 15 },
          { wch: 25 }, { wch: 15 }
        ];

        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'Membres');
        XLSX.writeFile(wb, `membres_${new Date().toISOString().split('T')[0]}.xlsx`);

        setSuccess('Export réussi');
        setTimeout(() => setSuccess(''), 3000);
      } else {
        setError(result.message || 'Erreur lors de l\'export');
      }
    } catch (err) {
      console.error(err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleImport = async () => {
    if (!selectedEglise) {
      setError('Veuillez sélectionner un temple pour l\'import');
      return;
    }

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const userStr = localStorage.getItem('user');
      if (!userStr) {
        navigate('/pages/signin2');
        return;
      }
      const user = JSON.parse(userStr);
      const userId = user.id || user.id_utilisateur;

      let successCount = 0;
      let errorCount = 0;
      const errors = [];

      const generatePassword = () => {
        const chars = 'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
        let pwd = '';
        for (let i = 0; i < 8; i++) {
          pwd += chars[Math.floor(Math.random() * chars.length)];
        }
        return pwd;
      };

      for (const [index, row] of importData.entries()) {
        try {
          const membreData = {
            nom: row.Nom || row.nom || '',
            prenom: row.Prénom || row.prenom || '',
            role: row.Rôle || row.role || 'Membre',
            gendre: row.Genre || row.gendre || 'Homme',
            contact: row.Contact || row.contact || '',
            email: row.Email || row.email || '',
            datenaiss: row['Date naissance'] || row.datenaiss || '',
            lieunaiss: row['Lieu naissance'] || row.lieunaiss || '',
            situtaion_maritale: row['Situation matrimoniale'] || row.situtaion_maritale || 'Célibataire',
            nb_enf: parseInt(row['Nombre enfants'] || row.nb_enf) || 0,
            date_bapt_eau: row['Date baptême eau'] || row.date_bapt_eau || '',
            date_bapt_saint: row['Date baptême esprit'] || row.date_bapt_saint || '',
            form_seculiere: row['Formation séculière'] || row.form_seculiere || '',
            form_eccle: row['Formation ecclésiastique'] || row.form_eccle || '',
            date_consecration: row['Date consécration'] || row.date_consecration || '',
            lieu_affectation: row['Lieu affectation'] || row.lieu_affectation || '',
            fonct_org: row['Fonction'] || row.fonct_org || '',
            date_depart: row['Date départ'] || row.date_depart || '',
            matri_eglise: selectedEglise,
            password: generatePassword()
          };

          if (!membreData.nom || !membreData.prenom || !membreData.gendre || !membreData.role) {
            errorCount++;
            errors.push(`Ligne ${index + 1}: Champs requis manquants (nom, prénom, genre, rôle)`);
            continue;
          }

          const response = await fetch(`https://josephsbusiness.com/api/membres.php?user_id=${userId}`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Accept': 'application/json'
            },
            body: JSON.stringify(membreData)
          });

          const text = await response.text();
          let result;
          try {
            result = JSON.parse(text);
          } catch (e) {
            errorCount++;
            errors.push(`Ligne ${index + 1}: Erreur de réponse serveur`);
            continue;
          }

          if (result.success) {
            successCount++;
          } else {
            errorCount++;
            errors.push(`Ligne ${index + 1}: ${result.message || 'Erreur inconnue'}`);
          }
        } catch (err) {
          errorCount++;
          errors.push(`Ligne ${index + 1}: ${err.message}`);
        }
      }

      const message = `${successCount} membres importés, ${errorCount} erreurs`;
      if (errors.length > 0) {
        console.error('Erreurs import:', errors);
        setError(message + ' - Consultez la console pour le détail');
      } else {
        setSuccess(message);
      }

      setImportStep('upload');
      setImportData([]);
      if (fileInputRef.current) fileInputRef.current.value = '';
      setTimeout(() => { setSuccess(''); setError(''); }, 5000);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const downloadTemplate = () => {
    const template = [{
      'Nom': 'Dupont',
      'Prénom': 'Jean',
      'Rôle': 'Pasteur',
      'Genre': 'Homme',
      'Contact': '066123456',
      'Email': 'jean.dupont@email.com',
      'Date naissance': '1980-01-01',
      'Lieu naissance': 'Libreville',
      'Situation matrimoniale': 'Marié(e)',
      'Nombre enfants': 2,
      'Date baptême eau': '2000-01-01',
      'Date baptême esprit': '2001-01-01',
      'Formation séculière': 'Master en Théologie',
      'Formation ecclésiastique': 'CFTS',
      'Date consécration': '2010-01-01',
      'Lieu affectation': 'Libreville',
      'Fonction': 'Pasteur principal',
      'Date départ': '2050-01-01'
    }];

    const ws = XLSX.utils.json_to_sheet(template);
    ws['!cols'] = [
      { wch: 20 }, { wch: 20 }, { wch: 15 }, { wch: 10 },
      { wch: 15 }, { wch: 25 }, { wch: 15 }, { wch: 20 },
      { wch: 20 }, { wch: 10 }, { wch: 15 }, { wch: 15 },
      { wch: 25 }, { wch: 25 }, { wch: 15 }, { wch: 20 },
      { wch: 25 }, { wch: 15 }
    ];

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Template');
    XLSX.writeFile(wb, 'template_membres.xlsx');
  };

  return (
    <>
      <style>{styles}</style>
      <Header />
      <div className="main main-app p-3 p-lg-4 dashboard-styled">
        <div className="container-fluid px-0">

          {/* En-tête */}
          <div className="header-modern mb-4 fade-in">
            <div className="header-left">
              <div className="header-icon-wrapper">
                <i className="ri-upload-line"></i>
              </div>
              <div>
                <h5 className="gradient-text">Import / Export des membres</h5>
                <div className="d-flex flex-wrap gap-2">
                  <span className="badge-modern">
                    <i className="ri-information-line"></i> Gérez les membres en masse
                  </span>
                  <span className="badge-modern">
                    <i className="ri-calendar-line"></i> {new Date().toLocaleDateString('fr-FR')}
                  </span>
                </div>
              </div>
            </div>
            <Button
              variant="outline-secondary"
              size="sm"
              className="btn-modern-outline"
              onClick={() => navigate('/membres/liste')}
            >
              <i className="ri-arrow-left-line"></i> Retour à la liste
            </Button>
          </div>

          {/* Cartes */}
          <Row className="g-4">
            {/* Export */}
            <Col md={6}>
              <Card className="card-modern border-primary h-100">
                <Card.Header className="card-header-modern">
                  <i className="ri-download-line"></i> Export
                </Card.Header>
                <Card.Body className="card-body-modern text-center">
                  <i className="ri-file-excel-line icon-big"></i>
                  <h6 className="fw-bold mt-3">Format Excel</h6>
                  <p className="text-muted small">
                    Téléchargez tous les membres avec leurs informations
                  </p>
                  <Button
                    variant="primary"
                    size="sm"
                    className="btn-modern-primary"
                    onClick={handleExport}
                    disabled={loading}
                  >
                    {loading ? (
                      <><Spinner size="sm" className="me-1" /> Export...</>
                    ) : (
                      <><i className="ri-download-line"></i> Exporter</>
                    )}
                  </Button>
                  {success && (
                    <div className="mt-3 small text-success">
                      <i className="ri-checkbox-circle-line me-1"></i> {success}
                    </div>
                  )}
                  {error && (
                    <div className="mt-3 small text-danger">
                      <i className="ri-error-warning-line me-1"></i> {error}
                    </div>
                  )}
                </Card.Body>
              </Card>
            </Col>

            {/* Import */}
            <Col md={6}>
              <Card className="card-modern border-secondary h-100">
                <Card.Header className="card-header-modern">
                  <i className="ri-upload-line"></i> Import
                </Card.Header>
                <Card.Body className="card-body-modern">
                  {/* Temple selector */}
                  <Form.Group className="mb-3">
                    <Form.Label className="form-label-modern">
                      <i className="ri-building-line me-1"></i> Temple d'affectation <span className="required">*</span>
                    </Form.Label>
                    {loadingEglises ? (
                      <div className="d-flex align-items-center">
                        <Spinner size="sm" className="me-2" style={{ color: COLORS.primary }} />
                        <span className="text-muted small">Chargement des temples...</span>
                      </div>
                    ) : (
                      <Form.Select
                        value={selectedEglise}
                        onChange={(e) => setSelectedEglise(e.target.value)}
                        className="form-select-modern"
                        disabled={importStep !== 'upload'}
                      >
                        <option value="">Sélectionner un temple</option>
                        {eglises.map(eg => (
                          <option key={eg.matricule} value={eg.matricule}>
                            {eg.nom} {eg.nom_parent ? `(Filiale de ${eg.nom_parent})` : '(Siège)'}
                          </option>
                        ))}
                      </Form.Select>
                    )}
                    <Form.Text className="form-text-modern">
                      Tous les membres importés seront rattachés à ce temple
                    </Form.Text>
                  </Form.Group>

                  {importStep === 'upload' && (
                    <div className="text-center">
                      <i className="ri-file-excel-line icon-big-success"></i>
                      <h6 className="fw-bold mt-2">Fichier Excel</h6>
                      <p className="text-muted small">
                        Sélectionnez un fichier Excel (.xlsx, .xls, .csv)
                      </p>
                      <div className="d-flex gap-2 justify-content-center">
                        <Button
                          variant="outline-secondary"
                          size="sm"
                          className="btn-modern-outline"
                          onClick={downloadTemplate}
                        >
                          <i className="ri-file-copy-line"></i> Template
                        </Button>
                        <Button
                          variant="primary"
                          size="sm"
                          className="btn-modern-primary"
                          onClick={() => fileInputRef.current?.click()}
                          disabled={!selectedEglise || loadingEglises}
                        >
                          <i className="ri-upload-line"></i> Choisir un fichier
                        </Button>
                      </div>
                      <input
                        type="file"
                        ref={fileInputRef}
                        accept=".xlsx,.xls,.csv"
                        onChange={handleFileUpload}
                        style={{ display: 'none' }}
                      />
                    </div>
                  )}

                  {importStep === 'preview' && (
                    <div className="fade-in">
                      <h6 className="fw-bold">Aperçu des données</h6>
                      <p className="text-muted small">
                        {importData.length} premières lignes à importer
                      </p>
                      <div className="table-preview">
                        <Table size="sm" className="small mb-3">
                          <thead>
                            <tr>
                              {Object.keys(importData[0] || {}).slice(0, 5).map(key => (
                                <th key={key}>{key}</th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            {importData.map((row, idx) => (
                              <tr key={idx}>
                                {Object.values(row).slice(0, 5).map((val, i) => (
                                  <td key={i}>{String(val).substring(0, 20)}</td>
                                ))}
                              </tr>
                            ))}
                          </tbody>
                        </Table>
                      </div>
                      <div className="d-flex gap-2 justify-content-center">
                        <Button
                          variant="outline-secondary"
                          size="sm"
                          className="btn-modern-outline"
                          onClick={() => {
                            setImportStep('upload');
                            setImportData([]);
                            if (fileInputRef.current) fileInputRef.current.value = '';
                          }}
                        >
                          Annuler
                        </Button>
                        <Button
                          variant="success"
                          size="sm"
                          className="btn-modern-success"
                          onClick={handleImport}
                          disabled={loading}
                        >
                          {loading ? (
                            <><Spinner size="sm" className="me-1" /> Import...</>
                          ) : (
                            'Confirmer l\'import'
                          )}
                        </Button>
                      </div>
                      {error && (
                        <Alert variant="danger" className="mt-3 small" style={{ borderRadius: '4px' }}>
                          <i className="ri-error-warning-line me-1"></i> {error}
                        </Alert>
                      )}
                      {success && (
                        <Alert variant="success" className="mt-3 small" style={{ borderRadius: '4px' }}>
                          <i className="ri-checkbox-circle-line me-1"></i> {success}
                        </Alert>
                      )}
                    </div>
                  )}
                </Card.Body>
              </Card>
            </Col>
          </Row>

          <Footer />
        </div>
      </div>
    </>
  );
}